import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import AdminLayout from "@/admin/AdminLayout";
import { adminApi } from "@/admin/AdminAuth";
import { formatGBP } from "@/lib/formatGBP";

const STATUS_COLORS = {
  paid: "bg-cyan-soft text-navy",
  dispatched: "bg-navy text-white",
  pending: "bg-amber-100 text-amber-800",
  failed: "bg-red-100 text-red-700",
  cancelled: "bg-silver/40 text-ink/70",
};

export default function AdminOrders() {
  const [stats, setStats] = useState(null);
  const [data, setData] = useState({ items: [], total: 0, page: 1, per_page: 25 });
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (status) params.set("status", status);
        if (q) params.set("q", q);
        params.set("page", String(page));
        params.set("per_page", "25");
        const [ordersRes, statsRes] = await Promise.all([
          adminApi.get(`/admin/orders?${params.toString()}`),
          adminApi.get("/admin/orders/stats"),
        ]);
        if (!alive) return;
        setData(ordersRes.data);
        setStats(statsRes.data);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [status, q, page]);

  const totalPages = Math.max(1, Math.ceil(data.total / data.per_page));

  return (
    <AdminLayout title="Orders">
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="admin-orders-kpis">
          <Kpi label="Orders today" value={stats.today_count} testId="kpi-orders-today" />
          <Kpi label="Revenue today" value={formatGBP(stats.today_revenue_pennies)} testId="kpi-revenue-today" />
          <Kpi label="Orders this month" value={stats.month_count} testId="kpi-orders-month" />
          <Kpi label="Pending fulfilment" value={stats.pending_fulfilment_count} accent testId="kpi-pending-fulfilment" />
        </div>
      )}

      <div className="rounded-2xl bg-white border border-silver/40 shadow-card overflow-hidden">
        <div className="p-4 sm:p-5 border-b border-silver/40 flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
            <input
              value={q}
              onChange={(e) => { setQ(e.target.value); setPage(1); }}
              placeholder="Search order #, email or name"
              data-testid="admin-orders-search"
              className="w-full h-10 rounded-xl border border-silver/70 bg-white pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-cyan focus:border-cyan transition"
            />
          </div>
          <select
            value={status}
            onChange={(e) => { setStatus(e.target.value); setPage(1); }}
            data-testid="admin-orders-status-filter"
            className="h-10 rounded-xl border border-silver/70 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-cyan transition"
          >
            <option value="">All statuses</option>
            <option value="paid">Paid</option>
            <option value="dispatched">Dispatched</option>
            <option value="pending">Pending</option>
            <option value="failed">Failed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>

        {loading ? (
          <div className="p-10 flex items-center justify-center text-muted text-sm">
            <Loader2 size={16} className="animate-spin mr-2" /> Loading…
          </div>
        ) : data.items.length === 0 ? (
          <div className="p-12 text-center text-muted text-sm" data-testid="admin-orders-empty">
            No orders match.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-orders-table">
              <thead className="bg-ice-light/60 text-muted text-xs uppercase tracking-widest">
                <tr>
                  <Th>Order #</Th>
                  <Th>Date</Th>
                  <Th>Customer</Th>
                  <Th>Email</Th>
                  <Th>Items</Th>
                  <Th>Total</Th>
                  <Th>Status</Th>
                  <Th className="text-right">Action</Th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((o) => (
                  <tr key={o.order_number} className="border-t border-silver/30 hover:bg-ice-light/40">
                    <Td className="font-mono text-navy font-semibold">{o.order_number}</Td>
                    <Td className="text-muted">{(o.created_at || "").slice(0, 10)}</Td>
                    <Td>{o.customer_name}</Td>
                    <Td className="text-muted">{o.customer_email}</Td>
                    <Td>{o.items_count}</Td>
                    <Td className="font-semibold tabular-nums">{formatGBP(o.amount_pennies)}</Td>
                    <Td>
                      <span
                        className={`inline-block rounded-full px-2.5 py-0.5 text-[11px] font-semibold capitalize ${STATUS_COLORS[o.status] || "bg-silver/40"}`}
                        data-testid={`admin-orders-row-status-${o.order_number}`}
                      >
                        {o.status}
                      </span>
                    </Td>
                    <Td className="text-right">
                      <Link
                        to={`/admin/orders/${o.order_number}`}
                        className="text-navy font-semibold hover:text-cyan transition-colors"
                        data-testid={`admin-orders-view-${o.order_number}`}
                      >
                        View
                      </Link>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {data.total > 0 && (
          <div className="p-4 border-t border-silver/40 flex items-center justify-between text-sm">
            <p className="text-muted">
              Showing {(page - 1) * data.per_page + 1}–
              {Math.min(page * data.per_page, data.total)} of {data.total}
            </p>
            <div className="flex items-center gap-1">
              <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
                className="h-9 w-9 inline-flex items-center justify-center rounded-lg border border-silver/60 disabled:opacity-40 hover:bg-ice"
                data-testid="admin-orders-prev">
                <ChevronLeft size={16} />
              </button>
              <span className="px-3 text-sm text-muted">{page} / {totalPages}</span>
              <button type="button" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}
                className="h-9 w-9 inline-flex items-center justify-center rounded-lg border border-silver/60 disabled:opacity-40 hover:bg-ice"
                data-testid="admin-orders-next">
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

function Kpi({ label, value, accent, testId }) {
  return (
    <div
      data-testid={testId}
      className={`rounded-2xl bg-white border border-silver/40 shadow-card p-4 ${accent ? "ring-1 ring-cyan/40" : ""}`}
    >
      <div className="text-[11px] uppercase tracking-widest text-muted">{label}</div>
      <div className="mt-2 text-2xl font-bold text-navy tabular-nums">{value}</div>
    </div>
  );
}
function Th({ children, className = "" }) {
  return <th className={`text-left font-semibold px-4 py-3 ${className}`}>{children}</th>;
}
function Td({ children, className = "" }) {
  return <td className={`px-4 py-3 align-middle ${className}`}>{children}</td>;
}
